import React, { useState, useEffect } from 'react';
import calendarService from '../../services/calendarService.js';
import './DatabaseTest.css';

const DatabaseTest = () => {
    const [connectionStatus, setConnectionStatus] = useState('Testing...');
    const [events, setEvents] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [statsLoading, setStatsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Test database connection on component mount
    useEffect(() => {
        testDatabaseConnection();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Debug effect to monitor stats state changes
    useEffect(() => {
        console.log('Stats state changed:', stats);
        console.log('Stats loading state:', statsLoading);
    }, [stats, statsLoading]);

    const testDatabaseConnection = async () => {
        try {
            setConnectionStatus('Testing API connection...');

            // First, try a simple ping to check basic connectivity
            const pingResult = await calendarService.pingApi();
            console.log('Ping result:', pingResult);

            if (!pingResult.reachable) {
                setConnectionStatus(`❌ API unreachable: ${pingResult.error}`);
                return;
            }

            // Now test the full API connection
            const isConnected = await calendarService.testConnection();
            if (isConnected) {
                setConnectionStatus(`✅ Connected to Calendar API (${pingResult.latency}ms)`);
                // Load initial data
                await loadEvents();
                await loadStats();
            } else {
                setConnectionStatus('❌ API connection failed');
            }
        } catch (error) {
            console.error('API connection test failed:', error);

            // Check for specific error types
            if (error.message.includes('502')) {
                setConnectionStatus('❌ API Server Error (502 Bad Gateway) - Backend server is down');
            } else if (error.message.includes('Failed to fetch')) {
                setConnectionStatus('❌ Network Error - API server unreachable');
            } else {
                setConnectionStatus(`❌ Connection error: ${error.message}`);
            }
        }
    };

    const loadEvents = async () => {
        try {
            setLoading(true);
            const allEvents = await calendarService.getAllEvents();
            setEvents(allEvents);
            setError(null);
        } catch (error) {
            console.error('Error loading events:', error);
            setError(`Failed to load events: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            setStatsLoading(true);
            console.log('Loading stats...');
            const eventStats = await calendarService.getEventStats();
            console.log('Stats loaded successfully:', eventStats);
            setStats(eventStats);
            setError(null);
        } catch (error) {
            console.error('Error loading stats:', error);
            setError(`Failed to load stats: ${error.message}`);
        } finally {
            setStatsLoading(false);
        }
    };

    const createTestEvent = async () => {
        try {
            setLoading(true);
            const testEvent = {
                Subject: 'Test Event from React App',
                StartTime: new Date(),
                EndTime: new Date(Date.now() + 60 * 60 * 1000), // 1 hour later
                IsAllDay: false,
                Description: 'This event was created directly from the React app using PostgreSQL',
                eventType: 'calendar',
                FamilyId: 1
            };

            const createdEvent = await calendarService.createEvent(testEvent);
            console.log('Test event created:', createdEvent);

            // Refresh the events list
            await loadEvents();
            await loadStats();
            setError(null);
        } catch (error) {
            console.error('Error creating test event:', error);
            setError(`Failed to create test event: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const deleteEvent = async (eventId) => {
        if (window.confirm('Are you sure you want to delete this event?')) {
            try {
                setLoading(true);
                await calendarService.deleteEvent(eventId);
                console.log('Event deleted:', eventId);

                // Refresh the events list
                await loadEvents();
                await loadStats();
                setError(null);
            } catch (error) {
                console.error('Error deleting event:', error);
                setError(`Failed to delete event: ${error.message}`);
            } finally {
                setLoading(false);
            }
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    return (
        <div className="database-test-container">
            <h2>Database Connection Test</h2>

            {/* Connection Status */}
            <div className="connection-status">
                <h3>API Status</h3>
                <p>{connectionStatus}</p>
                <button
                    onClick={testDatabaseConnection}
                    disabled={loading}
                    className="test-button"
                >
                    Test Connection
                </button>

                {connectionStatus.includes('502') && (
                    <div className="error-info">
                        <h4>🚨 API Server Issue Detected</h4>
                        <p>The backend API server at <code>prisma.tiggaa.co.uk</code> is currently down (502 Bad Gateway).</p>
                        <p><strong>What this means:</strong></p>
                        <ul>
                            <li>The API server is not responding to requests</li>
                            <li>All calendar operations will fail until it's restored</li>
                            <li>This is a backend infrastructure issue, not a frontend problem</li>
                        </ul>
                        <p><strong>Next steps:</strong></p>
                        <ul>
                            <li>Check if the backend server is running</li>
                            <li>Verify the server configuration and database connections</li>
                            <li>Check server logs for any error messages</li>
                            <li>Restart the backend service if necessary</li>
                        </ul>
                    </div>
                )}
            </div>

            {/* Error Display */}
            {error && (
                <div className="error-message">
                    <h3>Error</h3>
                    <p>{error}</p>
                </div>
            )}

            {/* Statistics */}
            <div className="stats-section">
                <h3>Calendar Statistics</h3>
                {statsLoading ? (
                    <p>Loading statistics...</p>
                ) : stats ? (
                    <div className="stats-grid">
                        <div className="stat-item">
                            <span className="stat-label">Total Events:</span>
                            <span className="stat-value">{stats.totalevents}</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-label">Appointments:</span>
                            <span className="stat-value">{stats.totalappointments}</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-label">Weekend Duties:</span>
                            <span className="stat-value">{stats.totalweekendduties}</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-label">Calendar Events:</span>
                            <span className="stat-value">{stats.totalcalendarevents}</span>
                        </div>
                    </div>
                ) : (
                    <p>No statistics available. Click "Test Connection" to load data.</p>
                )}
                <button
                    onClick={loadStats}
                    disabled={statsLoading}
                    className="action-button refresh"
                >
                    {statsLoading ? 'Loading...' : 'Refresh Stats'}
                </button>
            </div>

            {/* Actions */}
            <div className="actions-section">
                <h3>Database Actions</h3>
                <div className="action-buttons">
                    <button
                        onClick={createTestEvent}
                        disabled={loading}
                        className="action-button create"
                    >
                        Create Test Event
                    </button>
                    <button
                        onClick={loadEvents}
                        disabled={loading}
                        className="action-button refresh"
                    >
                        Refresh Events
                    </button>
                </div>
            </div>

            {/* Events List */}
            <div className="events-section">
                <h3>Calendar Events ({events.length})</h3>
                {loading ? (
                    <p>Loading events...</p>
                ) : events.length === 0 ? (
                    <p>No events found. Create a test event to get started!</p>
                ) : (
                    <div className="events-list">
                        {events.map((event) => (
                            <div key={event.id} className="event-item">
                                <div className="event-header">
                                    <h4>{event.Subject}</h4>
                                    <span className="event-type">{event.eventType || 'calendar'}</span>
                                </div>
                                <div className="event-details">
                                    <p><strong>Start:</strong> {formatDate(event.StartTime)}</p>
                                    <p><strong>End:</strong> {formatDate(event.EndTime)}</p>
                                    {event.Description && (
                                        <p><strong>Description:</strong> {event.Description}</p>
                                    )}
                                    {event.isAppointment && (
                                        <p><strong>Appointment:</strong> {event.parent} - {event.appointmentType}</p>
                                    )}
                                    {event.isWeekendDuty && (
                                        <p><strong>Weekend Duty:</strong> {event.assignedMemberId}</p>
                                    )}
                                </div>
                                <div className="event-actions">
                                    <button
                                        onClick={() => deleteEvent(event.id)}
                                        className="delete-button"
                                        disabled={loading}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DatabaseTest;
